export const DEFAULT_SORT = Object.freeze({ column: "need", direction: "desc" });
export const SORT_COLUMNS = ["name", "n", "fill", "ref", "vs", "open", "need"];

const DESCENDING_FIRST = ["need", "open", "n"];
const NUMERIC = ["n", "fill", "ref", "vs", "open", "need"];

export function compareKey(state, compare) {
  const params = new URLSearchParams();
  params.set("date", state.date || "");
  params.set("view", state.view || "");
  params.set("facility", state.facility || "all");
  params.set("threshold", state.threshold || "50");
  params.set("scope", state.scope || "group");
  params.set("compare", compare.mode || "classes");
  if (compare.mobile) params.set("layout", "mobile");
  return params.toString();
}

export function createCompareCache({ limit = 20 } = {}) {
  const entries = new Map();
  return {
    has: (key) => entries.has(key),
    get: (key) => entries.get(key),
    set(key, html) {
      entries.delete(key);
      entries.set(key, html);
      while (entries.size > limit) entries.delete(entries.keys().next().value);
    },
    clear: () => entries.clear(),
  };
}

export function createLatest() {
  let current = 0;
  return {
    next: () => {
      current += 1;
      return current;
    },
    isCurrent: (token) => token === current,
  };
}

export function isAbort(error) {
  return !!error && error.name === "AbortError";
}

export function createCompareLoader({ load, cache, createSignal = () => new AbortController() }) {
  let currentKey = null;
  const latest = createLatest();
  let inflight = null;

  const abortInflight = () => {
    if (inflight) inflight.abort();
    inflight = null;
  };

  return {
    get currentKey() {
      return currentKey;
    },
    request(key) {
      currentKey = key;
      const token = latest.next();
      abortInflight();
      if (cache.has(key)) {
        const html = cache.get(key);
        return Promise.resolve().then(() => (latest.isCurrent(token) ? { key, html, cached: true } : null));
      }
      const controller = createSignal();
      inflight = controller;
      return load(key, controller.signal).then(
        (html) => {
          if (inflight === controller) inflight = null;
          if (!latest.isCurrent(token) || key !== currentKey) return null;
          cache.set(key, html);
          return { key, html, cached: false };
        },
        (error) => {
          if (inflight === controller) inflight = null;
          if (isAbort(error) || !latest.isCurrent(token)) return null;
          throw error;
        },
      );
    },
    forget() {
      currentKey = null;
      latest.next();
      abortInflight();
    },
  };
}

export function scopeChanged(previous, next) {
  return previous !== undefined && previous !== next;
}

export function keepView(view, available) {
  return available.includes(view.mode) ? view : { panel: "attention", mode: "classes" };
}

export function nextSort(current, column) {
  if (current && current.column === column) return { column, direction: current.direction === "desc" ? "asc" : "desc" };
  return { column, direction: DESCENDING_FIRST.includes(column) ? "desc" : "asc" };
}

export function parseSortValue(column, raw) {
  if (raw === undefined || raw === null || raw === "") return null;
  if (!NUMERIC.includes(column)) return String(raw).toLowerCase();
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function compareValues(a, b) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

export function sortRows(rows, sort = DEFAULT_SORT) {
  const sign = sort.direction === "desc" ? -1 : 1;
  return [...rows].sort((left, right) => {
    if (!!left.pinned !== !!right.pinned) return left.pinned ? 1 : -1;
    const a = left.values[sort.column];
    const b = right.values[sort.column];
    const aMissing = a === null || a === undefined;
    const bMissing = b === null || b === undefined;
    if (aMissing !== bMissing) return aMissing ? 1 : -1;
    const primary = aMissing ? 0 : sign * compareValues(a, b);
    return primary || compareValues(left.values.name || "", right.values.name || "");
  });
}

export function matchesQuery(row, query) {
  const needle = (query || "").trim().toLowerCase();
  if (!needle) return true;
  return [row.name, row.sub].some((text) => (text || "").toLowerCase().includes(needle));
}

export function ariaSort(sort, column) {
  if (sort.column !== column) return "none";
  return sort.direction === "desc" ? "descending" : "ascending";
}
